import { useEffect, type RefObject } from "react";

/**
 * In-page anchors on the public surfaces (the Home motion layer, 2026-10-09; shared and
 * mounted once in the root for every public route on the founder's call of the same day):
 * a click on an `<a href="#…">` inside `root` scrolls to its target smoothly, lands the heading under
 * the header (the target's `scroll-margin-top`), writes the hash so the back button and
 * the waitlist's hash listener still work, and moves the focus to the target. Under
 * `prefers-reduced-motion: reduce` the scroll jumps. Her own scrolling is never touched.
 * A router `<Link to="#…">` renders `/es#…`, so the match is by resolved URL (same
 * origin and path, a hash), not by the literal `href`; the router's own click handler
 * stands down on a prevented event. Modified clicks (new tab, middle button) are left to
 * the browser. Back/forward between anchors (QA 2026-10-09; her approval the same day):
 * nothing restores the position of an entry this hook pushed — the router keeps scroll
 * restoration manual — so the hook brings the target into view at once, as a browser's
 * own restoration would, and the focus follows the hash so the keyboard and the reader
 * land where the screen did — unless a listener already claimed it, exactly as on a click.
 */
/** The id a hash names; a malformed escape (`#%E0`) is read literally rather than thrown. */
function hashId(hash: string): string {
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
}

/**
 * The clicks this hook claimed (`preventDefault` for its own scroll). A page handler that
 * must still act on its in-page link — the FAQ's arrival on the topic already in the URL —
 * tells the hook's cancellation apart from anyone else's with this, instead of standing
 * down on every `defaultPrevented`.
 */
const claimedClicks = new WeakSet<Event>();
export function claimedBySmoothAnchors(event: Event): boolean {
  return claimedClicks.has(event);
}

export function useSmoothAnchors(root: RefObject<HTMLElement | null>, { enabled = true }: { enabled?: boolean } = {}) {
  useEffect(() => {
    const el = root.current;
    if (!el || !enabled) return;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = (event.target as Element | null)?.closest?.("a[href*=\"#\"]");
      if (!(link instanceof HTMLAnchorElement)) return;
      // Not an in-page anchor: a new tab, a file to save, another path or another search —
      // the browser and the router own those (the QA's list before generalising).
      if ((link.target && link.target !== "_self") || link.hasAttribute("download")) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search || url.hash.length < 2) return;
      const id = hashId(url.hash);
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      claimedClicks.add(event);
      const reduced = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
      // WHY `history.pushState` and not the router's `navigate`: a hash-only navigation
      // through the router re-renders the route and runs its scroll restoration, which
      // jumps to the hash — the very jump this hook replaces. A pushed entry keeps the
      // back button and the `hashchange` listeners (the waitlist's focus) working, and
      // the router's own `popstate` handling still owns a return to a different path.
      if (window.location.hash !== `#${id}`) {
        // The router's own `history.state` (its key and index) travels with the entry, so a
        // return through the history is one the router can still account for (T714).
        window.history.pushState(window.history.state, "", `#${id}`);
        window.dispatchEvent(new HashChangeEvent("hashchange"));
      }
      focusTarget(target);
    };
    // A listener on `hashchange` may already have claimed the focus inside the target (the
    // waitlist focuses its own heading); only then is the section itself focused.
    const focusTarget = (target: HTMLElement) => {
      if (target.contains(document.activeElement) && document.activeElement !== target) return;
      if (!target.hasAttribute("tabindex")) target.tabIndex = -1;
      target.focus({ preventScroll: true });
    };
    const onPopState = () => {
      const id = hashId(window.location.hash);
      const target = id ? document.getElementById(id) : null;
      if (!target || !el.contains(target)) return;
      // A fragment the browser navigated to itself (a modified click, a link left to it)
      // also fires `popstate`, with the target already in place: only a target that is
      // elsewhere is moved.
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      if (Math.abs(target.getBoundingClientRect().top - margin) > 1) {
        target.scrollIntoView({ behavior: "auto", block: "start" });
      }
      focusTarget(target);
    };
    el.addEventListener("click", onClick);
    window.addEventListener("popstate", onPopState);
    return () => {
      el.removeEventListener("click", onClick);
      window.removeEventListener("popstate", onPopState);
    };
  }, [root, enabled]);
}
